/**
 * Visibility rules for the row context-menu items (PSTUDIO-3163 / PSTUDIO-3164).
 *
 * Extracted from draggable-row.tsx so the rules are unit-testable and shared by
 * every trigger (index cell, group-header row, data cell). For a group-header
 * row the destructive / inapplicable items collapse to nothing, leaving the
 * curated safe set Copy / Cut / Ungroup / Delete — see the ticket rationale:
 * a group is a container, not an executable node, and reusing the row-level
 * Delete/Clear/Paste handlers on a group id corrupts membership.
 */
export interface RowContextMenuFlags {
  /** Target row is a group-header row. */
  isGroupNode: boolean;
  /** Target row is a member (sub-row) of a group. */
  isSubRow: boolean;
  /** A continuous multi-row selection that includes the target row. */
  isPartOfMultiSelection: boolean;
  /** Exactly one row is selected (the right-clicked one) — a single-node group is legal. */
  isSingleRowSelection: boolean;
  /** The current selection is eligible to be grouped (no nesting). */
  canGroupSelection: boolean;
}

export const isGoToNodeItemVisible = (
  flags: Pick<RowContextMenuFlags, 'isGroupNode'> & { hasSearchQuery: boolean }
): boolean => flags.hasSearchQuery && !flags.isGroupNode;

/** Paste rows is positional around a plain row; meaningless/destructive on a group header. */
export const isPasteItemVisible = (
  flags: Pick<RowContextMenuFlags, 'isGroupNode'>
): boolean => !flags.isGroupNode;

/** Inserting relative to a group header would split the contiguous group block. */
export const isInsertItemVisible = (
  flags: Pick<RowContextMenuFlags, 'isGroupNode'>
): boolean => !flags.isGroupNode;

/** A group is a container, not an executable node — it cannot be an NL entry point. */
export const isNlEntryNodeItemVisible = (
  flags: Pick<RowContextMenuFlags, 'isGroupNode'>
): boolean => !flags.isGroupNode;

/**
 * True when the row the menu was opened on is the ONLY selected row, which is
 * what lets "Group Nodes" be offered for a single node (PSTUDIO-4198). A
 * right-click on an UNSELECTED row replaces the selection with that row, and a
 * right-click on a row that is already selected leaves the selection as it is
 * (`openRowContextMenu` in draggable-row.tsx and the `onContextMenu` of
 * cell.tsx), so a multi-row selection stays a multi-row selection. Both
 * handlers select the clicked row, which means `isRowSelected` is never false
 * with a count of 1 when the menu comes from them; the half is kept so that a
 * selection belonging to a different row can never enable the item.
 */
export const resolveIsSingleRowSelection = ({
  selectedCount,
  isRowSelected,
}: {
  selectedCount: number;
  isRowSelected: boolean;
}): boolean => selectedCount === 1 && isRowSelected;

export const isGroupNodesItemVisible = (flags: RowContextMenuFlags): boolean =>
  (flags.isPartOfMultiSelection || flags.isSingleRowSelection) &&
  !flags.isSubRow &&
  !flags.isGroupNode &&
  flags.canGroupSelection;

/**
 * Ungroup is offered ONLY on the group-header row (its curated context menu +
 * the inline toolbar button). PSTUDIO-3160 (B): right-clicking a subRow must
 * NOT offer Ungroup — from a single member it read as destroying the whole
 * group, which is confusing; the group-header is the one canonical entry point.
 */
export const isUngroupNodesItemVisible = (
  flags: Pick<RowContextMenuFlags, 'isGroupNode'>
): boolean => flags.isGroupNode;

/** Clear resets a row to defaults — on a group header it would wipe settings.subRows/groupName. */
export const isClearItemVisible = (
  flags: Pick<RowContextMenuFlags, 'isGroupNode'>
): boolean => !flags.isGroupNode;

/** A single menu entry: an action item key, or a visual divider. */
export type RowContextMenuItemKey =
  | 'go-to-node'
  | 'copy'
  | 'cut'
  | 'paste-above'
  | 'paste-replace'
  | 'paste-below'
  | 'insert-above'
  | 'insert-below'
  | 'nl-entry'
  | 'group'
  | 'ungroup'
  | 'clear'
  | 'delete';

export type RowContextMenuEntry = RowContextMenuItemKey | 'divider';

export interface RowContextMenuLayoutFlags extends RowContextMenuFlags {
  /** A non-empty search query is active (enables "Go to node"). */
  hasSearchQuery: boolean;
  /** The row carries a numeric node number (required for "Go to node"). */
  hasRowNumber: boolean;
}

/**
 * Single source of truth for the ordered row context-menu entries, INCLUDING
 * dividers. The RowContextMenu component renders straight from this array, so
 * the unit test on this function verifies exactly what the user sees — closing
 * the divider byte-identity gap (PSTUDIO-3163 / PSTUDIO-3164).
 *
 * Invariants (guaranteed by construction, asserted exhaustively in the test):
 *   • never a leading, trailing, or adjacent divider for ANY flag combination;
 *   • `copy`, `cut`, `delete` are always present;
 *   • a group-header row collapses to [copy, cut, divider, ungroup, divider, delete].
 */
export function buildRowContextMenuLayout(
  flags: RowContextMenuLayoutFlags
): RowContextMenuEntry[] {
  const showGoTo = isGoToNodeItemVisible(flags) && flags.hasRowNumber;
  const showPaste = isPasteItemVisible(flags);
  const showInsert = isInsertItemVisible(flags);
  const showNlEntry = isNlEntryNodeItemVisible(flags);
  const showGroup = isGroupNodesItemVisible(flags);
  const showUngroup = isUngroupNodesItemVisible(flags);
  const showClear = isClearItemVisible(flags);

  // Only needed when the Group/Ungroup block is the FIRST thing after Copy/Cut
  // (i.e. a group-header row, where Paste/Insert/NL are all hidden); otherwise
  // Ungroup stays glued under NL entry with no extra divider, as it always has.
  const dividerBeforeGroupBlock =
    (showGroup || showUngroup) && !(showInsert || showNlEntry);

  const entries: RowContextMenuEntry[] = [];
  if (showGoTo) {
    entries.push('go-to-node', 'divider');
  }
  entries.push('copy', 'cut');
  if (showPaste) {
    entries.push('paste-above', 'paste-replace', 'paste-below');
  }
  if (showInsert) {
    entries.push('divider', 'insert-above', 'insert-below');
  }
  if (showNlEntry) {
    entries.push('nl-entry');
  }
  if (dividerBeforeGroupBlock) {
    entries.push('divider');
  }
  if (showGroup) {
    entries.push('group');
  }
  if (showUngroup) {
    entries.push('ungroup');
  }
  entries.push('divider');
  if (showClear) {
    entries.push('clear');
  }
  entries.push('delete');
  return entries;
}
